import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/**
 * A tab value that lives in the URL (`?tab=...`), so a reload, the back button
 * and a shared link land on the same tab. An unknown or missing value falls
 * back to `defaultValue`; selecting the default removes the param.
 */
export function useUrlTab<T extends string>(
  param: string,
  values: readonly T[],
  defaultValue: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);
  const value = raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (previous) => {
          const params = new URLSearchParams(previous);
          if (next === defaultValue) {
            params.delete(param);
          } else {
            params.set(param, next);
          }
          return params;
        },
        { replace: true },
      );
    },
    [defaultValue, param, setSearchParams],
  );

  return [value, setValue];
}
